/**
 * Shared OTC deal display logic used by both Blotter and Inspector.
 * Single source of truth for field value resolution and formatting.
 */

import { getNotionalCurrencies } from '@src/components/OMS/ManualDealTradeView/utils';
import type { DealFixedRateLoanData, DealFloatingRateLoanData, DealForwardData, OTCDealData } from '@src/types/OTC';
import {
  formatPercent,
  formattedDateAtMinutes,
  isOTCForwardSecurity,
  isOTCOptionSecurity,
  percentToBps,
  type Currency,
  type OTCFixedRateLoanSecurity,
  type OTCFloatingRateLoanSecurity,
  type OTCForwardSecurity,
  type OTCOptionSecurity,
  type Security,
} from '@talos/kyoko';
import type { ValueGetterParams } from 'ag-grid-community';
import { formatInTimeZone } from 'date-fns-tz';

/** Forward or vanilla option OTC derivative (template or instantiated); used for unit-notional display. */
function isOtcForwardOrVanillaOptionSecurity(
  security: Security | undefined
): security is OTCForwardSecurity | OTCOptionSecurity {
  if (security == null || typeof security !== 'object') {
    return false;
  }
  const s = security as Security;
  return isOTCForwardSecurity(s) || isOTCOptionSecurity(s);
}

/** Display value for size/amount fields */
interface SizeDisplayValue {
  value: string;
  currency: string;
}

/** Display value for rate (percentage) */
interface RateDisplayValue extends SizeDisplayValue {
  value: string;
  currency: '%';
}

/** Display value for spread (bps) */
interface SpreadDisplayValue extends SizeDisplayValue {
  value: string;
  currency: 'bps';
}

/**
 */
export function getOtcDealQuantityDisplayValue(deal: OTCDealData): SizeDisplayValue {
  return {
    value: deal.DealInfo.EnteredQuantity ?? '',
    currency: deal.DealInfo.EnteredQuantityAsset || (deal.DealInfo.EnteredQuantity === '1' ? 'Unit' : 'Units'),
  };
}

/**
 * Loan Amount - Inspector logic: DealInfo.LoanAmount, currency from productTemplate.ProductDetails.BorrowedAsset
 */
export function getOtcDealLoanAmountDisplayValue(
  deal: DealFixedRateLoanData | DealFloatingRateLoanData,
  dealSecurity: OTCFixedRateLoanSecurity | OTCFloatingRateLoanSecurity
): SizeDisplayValue {
  const loanAmount = deal.DealInfo.LoanAmount ?? '';
  const borrowedAsset = dealSecurity.ProductDetails.BorrowedAsset ?? '';
  return { value: loanAmount, currency: borrowedAsset };
}

/**
 * Rate - Inspector logic: rate * 100 for percentage display
 */
export function getOtcDealRateDisplayValue(deal: DealFixedRateLoanData): RateDisplayValue | null {
  const rate = deal.DealInfo.Rate;
  if (rate == null || rate === '') {
    return null;
  }
  const rateAsPercentage = formatPercent(rate);
  return { value: rateAsPercentage ?? '', currency: '%' };
}

/**
 * Spread - Inspector logic: spread stored as decimal (0.0001 = 1 bps), multiply by 10000 for display
 */
export function getOtcDealSpreadDisplayValue(
  deal: DealFixedRateLoanData | DealFloatingRateLoanData | DealForwardData
): SpreadDisplayValue | null {
  const spread = deal.DealInfo.Spread;
  if (spread == null || spread === '') {
    return null;
  }
  const spreadAsBps = percentToBps(spread);
  return { value: spreadAsBps, currency: 'bps' };
}

/**
 * Submit Time blotter value: prefers SubmitTime, falls back to Timestamp when absent (e.g. older payloads).
 */
export function getOtcDealSubmitTimeValue(deal: OTCDealData | undefined): string | undefined {
  if (!deal) {
    return undefined;
  }
  const submitTime = deal.SubmitTime;
  if (submitTime && submitTime !== '0') {
    return submitTime;
  }
  return deal.Timestamp;
}

/**
 * Display name for “user” in blotter and deal cards: prefers {@link OTCDealData.UpdateUser},
 * then {@link OTCDealData.CreateUser}. Empty or whitespace-only `UpdateUser` falls back to `CreateUser`.
 */
export function getOtcDealDisplayUser(deal: Pick<OTCDealData, 'UpdateUser' | 'CreateUser'>): string | undefined {
  const fromUpdate = deal.UpdateUser?.trim();
  if (fromUpdate) {
    return fromUpdate;
  }
  const fromCreate = deal.CreateUser?.trim();
  return fromCreate || undefined;
}

/**
 * Formats a deal ISO timestamp in the given IANA zone (`EEE yyyy-MM-dd HH:mm zzz`), matching
 * ManualDealDateControls and the OTC Inspector contract-dates section.
 */
export function formatOtcDealDateTimeInTimezone(
  value: string | number | null | undefined,
  timezone: string = 'UTC'
): string {
  if (value == null || value === '' || value === '0') {
    return '—';
  }
  try {
    const date = typeof value === 'string' ? new Date(value) : new Date(value);
    if (isNaN(date.getTime())) {
      return formattedDateAtMinutes(value);
    }
    return formatInTimeZone(date, timezone, 'EEE yyyy-MM-dd HH:mm zzz');
  } catch {
    return formattedDateAtMinutes(value);
  }
}

/** Context for resolving unit notional (blotter grid or Inspector). */
export interface OtcDealUnitNotionalContext {
  securitiesBySymbol?: Map<string, Security>;
  currenciesBySymbol?: Map<string, Currency>;
}

/**
 * Unit notional: amount from resolved deal symbol security `ProductDetails.UnitNotionalAmount`;
 * display currency from product payoff (same rules as {@link getNotionalCurrencies} / forward & option quantity UIs).
 * Increment from `currenciesBySymbol` for the resolved currency.
 */
export function getOtcDealUnitNotionalDisplayValue(
  deal: OTCDealData,
  context: OtcDealUnitNotionalContext
): {
  value: string;
  currency: string;
  increment?: string;
} {
  const rawSecurity = deal.Symbol ? context.securitiesBySymbol?.get(deal.Symbol) : undefined;
  if (!isOtcForwardOrVanillaOptionSecurity(rawSecurity)) {
    return { value: '', currency: '', increment: undefined };
  }
  const { ProductDetails } = rawSecurity;
  const unitAmount = ProductDetails.UnitNotionalAmount;
  const { unitNotionalCurrency } = getNotionalCurrencies(
    ProductDetails.PayoffType,
    ProductDetails.BaseAsset,
    ProductDetails.QuoteAsset
  );
  const currency = unitNotionalCurrency ?? '';
  const currencyInfo = currency ? context.currenciesBySymbol?.get(currency) : undefined;
  return {
    value: unitAmount ?? '',
    currency,
    increment: currencyInfo?.DefaultIncrement,
  };
}

/** AG Grid size column: same display rules as {@link getOtcDealUnitNotionalDisplayValue}. */
export function getOtcDealUnitNotionalSizeValue(params: ValueGetterParams<OTCDealData>): {
  value: string;
  currency: string;
  increment?: string;
} {
  const data = params.data;
  if (!data) {
    return { value: '', currency: '' };
  }
  return getOtcDealUnitNotionalDisplayValue(data, {
    securitiesBySymbol: params.context?.current?.securitiesBySymbol,
    currenciesBySymbol: params.context?.current?.currenciesBySymbol,
  });
}
